import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import TrashIcon from '~/assets/icons/trash.svg';
import EditIcon from '~/assets/icons/edit.svg';
import DuplicateIcon from '~/assets/icons/duplicate.svg';
import { useEditorStore, EditorChapter, EditorTraining } from '~/shell/Store';
import ConfirmModal from './ConfirmModal';

export const TrashIconForButton = styled(TrashIcon)``;
export const EditIconForButton = styled(EditIcon)``;
export const DuplicateIconForButton = styled(DuplicateIcon)``;

const PopupContainer = styled.div`
  position: absolute;
  background-color: #ffffff;
  border: 1px solid #ccc;

  box-shadow: 0px 8px 9px 0px #00000036;
  box-shadow: 0px 30px 30px 0px #00000008;
  box-shadow: 0px -2px 4px 0px #0000000d;

  padding: 5px 0px 0px 0px;
  gap: 0px;
  border-radius: 5px;
  opacity: 0px;

  z-index: 1000;
  flex-direction: column;
  display: flex;
  margin-top: 10px;
  margin-left: -20px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  margin: 3px;
  padding: 7px;
  background-color: #fff;

  font-family: Raleway;
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;

  white-space: nowrap;

  svg {
    margin-right: 5px;
  }

  &:hover {
    background-color: #f5f5f5;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

interface PopupMenuProps {
  isOpen: boolean;
  anchorElement: HTMLElement | null;
  onClose: () => void;
  data: EditorChapter['chapterSequences'][number];
  setIsRename?: (data: boolean) => void;
  id?: string;
  selectItem?: (
    data: EditorChapter['chapterSequences'][number]['sequence'],
    _training?: EditorTraining
  ) => void;
}

const PopupMenu: React.FC<PopupMenuProps> = ({
  isOpen,
  anchorElement,
  onClose,
  data,
  setIsRename,
  id,
  selectItem
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [disabled, setDisabled] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const { removeHistoryForSequence } = useEditorStore();

  const { training, setTraining, apiService, sequence, setSequence } =
    useEditorStore();

  useEffect(() => {
    if (!isOpen) {
      setShowConfirmModal(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const totalChapterSequences = training!.chapters.reduce(
      (acc: number, chapter: EditorChapter) => {
        return acc + chapter.chapterSequences.length;
      },
      0
    );

    if (totalChapterSequences < 2) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [training, id, isOpen]);

  useEffect(() => {
    if (anchorElement) {
      const rect = anchorElement.getBoundingClientRect();

      let top = rect.bottom - 60;
      const left = rect.left - 80;

      if (top + 140 >= window.innerHeight) {
        top = top - 140;
      }

      if (top < 0) {
        top = 0;
      }

      setPosition({ top, left });
    }
  }, [anchorElement, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showConfirmModal) return;

      if (
        anchorElement &&
        !anchorElement.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest('.popup-container')
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, anchorElement, onClose, showConfirmModal]);

  if (!isOpen || !anchorElement) return null;

  const duplicateClickHandler = () => {
    onClose();

    if (apiService) {
      apiService
        .copySequence(data.id!, training!.id as number)
        .then(
          (newSequence: {
            chapterSequence: EditorChapter['chapterSequences'][number];
          }) => {
            const mergedSequence = {
              ...data,
              id: newSequence.chapterSequence.id,
              position: data.position! + 1,
              sequence: {
                ...data.sequence,
                ...newSequence.chapterSequence.sequence
              }
            };

            const updatedChapters = training!.chapters.map(
              (chapter: EditorChapter) => {
                if (
                  chapter.chapterSequences.some(
                    (seq: EditorChapter['chapterSequences'][number]) =>
                      seq.sequence.id === data.sequence.id
                  )
                ) {
                  const index = chapter.chapterSequences.findIndex(
                    (seq: EditorChapter['chapterSequences'][number]) =>
                      seq.sequence.id === data.sequence.id
                  );
                  const newChapterSequences = [
                    ...chapter.chapterSequences.slice(0, index + 1),
                    mergedSequence,
                    ...chapter.chapterSequences.slice(index + 1)
                  ];
                  return { ...chapter, chapterSequences: newChapterSequences };
                }
                return chapter;
              }
            );

            setTraining({ ...training!, chapters: updatedChapters });
          }
        );
    }
  };

  const isLegallyRequired = data?.sequence?.legallyRequired === true;

  const deleteClickHandler = () => {
    if (popupRef && popupRef.current) {
      popupRef.current.style.display = 'none';
    }
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    const updatedChapters = training!.chapters.map((chapter: EditorChapter) => {
      return {
        ...chapter,
        chapterSequences: chapter.chapterSequences.filter(
          (seq: EditorChapter['chapterSequences'][number]) =>
            seq.sequence.id !== data.sequence.id
        )
      };
    });

    if (sequence?.id == id && selectItem) {
      const allSequences = training!.chapters.flatMap(
        (chapter: EditorChapter) => chapter.chapterSequences
      );
      const currentIndex = allSequences.findIndex(
        (seq: EditorChapter['chapterSequences'][number]) =>
          seq.sequence.id === id
      );
      const previousSequence =
        currentIndex > 0 ? allSequences[currentIndex - 1] : null;
      const nextSequence =
        currentIndex < allSequences.length - 1
          ? allSequences[currentIndex + 1]
          : null;

      const _sequence = previousSequence || nextSequence;

      selectItem(_sequence!.sequence, {
        ...training!,
        chapters: updatedChapters
      });
    }

    removeHistoryForSequence(sequence?.id as number);

    apiService && apiService.deleteSequence(data.id!);
    setTraining({ ...training!, chapters: updatedChapters });
  };

  return (
    <>
      {isOpen &&
        anchorElement &&
        ReactDOM.createPortal(
          <PopupContainer
            className="popup-container"
            style={{ top: position.top, left: position.left }}
            ref={popupRef}
          >
            {setIsRename && (
              <Button onClick={() => setIsRename(true)}>
                <EditIconForButton /> Rename
              </Button>
            )}
            <Button onClick={duplicateClickHandler}>
              <DuplicateIconForButton /> Duplicate
            </Button>
            <Button onClick={deleteClickHandler} disabled={disabled}>
              <TrashIconForButton
                style={{ marginLeft: '-5px', fill: '#2E2E2E' }}
              />{' '}
              Delete
            </Button>
          </PopupContainer>,
          document.body
        )}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          onClose();
        }}
        onConfirm={handleConfirmDelete}
        title={isLegallyRequired ? 'Delete This Slide?' : 'Delete Sequence'}
        message={
          isLegallyRequired
            ? 'This slide may contain legally required content. Deleting it may make your course non-compliant. Would you like to proceed?'
            : 'Are you sure you want to delete this sequence? This action cannot be undone.'
        }
      />
    </>
  );
};

export default PopupMenu;
